import Loader from 'components/v2/Loader/Loader';
import { useReportItem } from 'modules/reports/hooks/use-report-item';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useReportContext } from '../../../../modules/reports/context/ReportContext';
import Link from '../../Link/Link';
import NativeLink from '../../NativeLink/NativeLink';
import css from './attributes.module.scss';
import { Attribute, Section } from '../../../../modules/reports/types';
import Spacer from 'components/v2/Spacer/Spacer';
import clsx from 'clsx';
import { useTranslation } from 'utils/translation';
import Icon from 'components/v2/Icon/Icon';
import ServerDataError from '../../Error/ServerDataError';
import QueryResult from '../../Query/QueryResult';
import { route } from '../../../../router';
import DeviceCover from '../DeviceCover';
import { applyFilters, Filters } from '../../../../core';
import Typography from '../../Typography/Typography';
import DeviceQrCode from './DeviceQrCode';

const AttributeValue = (attribute: Attribute) => {
  const { t } = useTranslation();

  if (attribute.name === 'device_type' && attribute?.reference?.value) {
    return (
      <Link
        to={route('assets.types.details', {
          id: Number(attribute.reference.value),
        })}
      >
        {attribute.value}
      </Link>
    );
  }

  return attribute.value || t('attribute.not_available');
};

const Attributes = ({ attributes }: { attributes: Attribute[] }) => {
  return (
    <ul className={css.attributes}>
      {attributes.map((item) => (
        <li
          className={clsx(css.item, `report_item__attribute-${item.name}`)}
          key={item.name}
        >
          <span className={css.label}>{item.label}</span>
          <span className={css.value}>
            <AttributeValue {...item} />
          </span>
        </li>
      ))}
    </ul>
  );
};

type Props = {
  id: number;
};

const filterForSection = (attrs: Attribute[], name: string) =>
  attrs.filter((item) => item.section === name);

const CustomSection = ({
  section,
  attributes,
}: {
  section: Section;
  attributes: Attribute[];
}) => (
  <div className={css.section}>
    <Typography variant="h2" uppercase>
      {section.name}
    </Typography>
    <Spacer size={2} />
    <Attributes attributes={filterForSection(attributes, section.id)} />
    {Array.from(section.children || []).map((item) => (
      <CustomSection key={item.id} attributes={attributes} section={item} />
    ))}
  </div>
);

const DeviceAttributes = ({ id }: Props) => {
  const { data, isLoading, isError } = useReportItem(id);

  if (isLoading || !data) {
    return <Loader />;
  }

  if (isError) {
    return <ServerDataError />;
  }

  const summary = data!.attributes.filter((item) => item.section === 'summary');
  const rest = data!.attributes.filter((item) => null === item.section);
  const address = data!.attributes.filter((item) => 'address' === item.name)[0];
  const mapUrl = data!.attributes.filter(
    (item) => 'location_map_url' === item.name,
  )[0];
  const customSections = data?.sections || [];

  const otherAttributes = [...rest];
  const imageStyle = applyFilters<'qr' | 'cover'>(
    Filters.assets.contextBar.imageStyle,
    'qr',
  );

  if (customSections.length > 0) {
    return (
      <div
        className={clsx(
          css.root,
          css['variant-default'],
          css['variant-custom-sections'],
        )}
      >
        {customSections.map((item) => (
          <CustomSection
            key={item.id}
            attributes={data.attributes}
            section={item}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={clsx(css.root, css['variant-default'])}>
      <div className={css.section}>
        <Attributes attributes={summary} />
        <div className={clsx(css.image, css['with-margin'])}>
          {imageStyle === 'qr' && <DeviceQrCode value={data!.identifier} />}
        </div>
      </div>
      <div className={css.section}>
        <Attributes attributes={otherAttributes} />
      </div>
      {address && mapUrl && (
        <>
          <Spacer size={2} />
          <Attributes
            attributes={[
              {
                ...address,
                value: (
                  <NativeLink
                    color="primary"
                    icon={<Icon name="fa-regular fa-location-dot fa-2x" />}
                    href={String(mapUrl.value)}
                    target="_blank"
                  >
                    {address.value}
                  </NativeLink>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export const DeviceAttributesVertical = ({
  id,
  children,
}: React.PropsWithChildren<Props>) => {
  const { t } = useTranslation();
  const report = useReportContext();
  const itemQuery = useReportItem(id);
  const navigate = useNavigate();

  const onDetailsClick = () => {
    navigate(`/assets/report/${report.id}/view/${id}`);
  };

  const imageStyle = applyFilters<'qr' | 'cover'>(
    Filters.assets.contextBar.imageStyle,
    'qr',
  );

  return (
    <QueryResult
      {...itemQuery}
      render={(data) => {
        const customSections = data?.sections || [];
        const summary = data!.attributes.filter(
          (item) => item.section === 'summary',
        );
        const rest = data!.attributes.filter((item) => null === item.section);

        if (customSections.length > 0) {
          return (
            <div
              className={clsx(
                css.root,
                css['variant-vertical'],
                css['variant-custom-sections'],
              )}
            >
              <div className={css.image}>
                {imageStyle === 'qr' ? (
                  <DeviceQrCode
                    onClick={onDetailsClick}
                    value={data!.identifier}
                  />
                ) : null}
                {imageStyle === 'cover' ? <DeviceCover id={id} /> : null}
                <Spacer size={4} />
              </div>
              {customSections.map((item) => (
                <CustomSection
                  key={item.id}
                  attributes={data.attributes}
                  section={item}
                />
              ))}
            </div>
          );
        }

        return (
          <div className={clsx(css.root, css['variant-vertical'])}>
            <div className={css.image}>
              {imageStyle === 'qr' ? (
                <DeviceQrCode
                  onClick={onDetailsClick}
                  value={data!.identifier}
                />
              ) : null}
              {imageStyle === 'cover' ? <DeviceCover id={id} /> : null}
            </div>
            <Spacer size={2} />
            <Link
              size="small"
              className="link"
              to={`/assets/report/${report.id}/view/${id}`}
            >
              {t('assets.detail_page')}
            </Link>
            <Spacer size={4} />
            <div className={css.section}>
              <Attributes attributes={summary} />
            </div>
            <div className={css.section}>
              <Attributes attributes={rest} />
            </div>
            {children}
          </div>
        );
      }}
    />
  );
};

export default DeviceAttributes;
